import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  isNotionPushPreview,
  type NotionPushPreview,
  useGetNotionPushStatusQuery,
  usePushToNotionMutation,
} from '@/services/api/bulkAgentThreadsServiceEnhanced';
import { getApiErrorMessage } from '@/utils/api-error';

// Extract a Notion page UUID from a raw URL or ID string
const normalizeNotionPageId = (raw: string) => {
  const trimmed = raw.trim();
  if (!trimmed) throw new Error('Please paste a Notion page ID or URL.');

  let value = trimmed;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      const url = new URL(value);
      value =
        url.searchParams.get('p') ||
        url.searchParams.get('page_id') ||
        url.searchParams.get('pageId') ||
        url.pathname.split('/').filter(Boolean).slice(-1)[0] ||
        value;
    } catch {
      // fall through and try regex extraction
    }
  }

  const uuidMatch = value.match(
    /([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/,
  );
  if (uuidMatch?.[1]) return uuidMatch[1].toLowerCase();

  const hex32Match = value.match(/([0-9a-fA-F]{32})/);
  if (hex32Match?.[1]) {
    const h = hex32Match[1].toLowerCase();
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(
      16,
      20,
    )}-${h.slice(20)}`;
  }

  throw new Error(
    "Invalid Notion page ID. Paste the UUID (e.g. '312e7a32-2002-81d9-977b-fdd366a59b4f') or a Notion URL.",
  );
};

export interface NotionPushModalProps {
  open: boolean;
  onClose: () => void;
  operationId: string;
  detail: {
    notion_page_url?: string | null;
    notion_push_status?: string | null;
    notion_push_error?: string | null;
  };
  onPushComplete: () => void;
  onPushStatusChange: (status: 'idle' | 'pushing' | 'done') => void;
}

export const NotionPushModal: React.FC<NotionPushModalProps> = ({
  open,
  onClose,
  operationId,
  detail,
  onPushComplete,
  onPushStatusChange,
}) => {
  const [targetPageId, setTargetPageId] = React.useState('');
  const [notionPreview, setNotionPreview] =
    React.useState<NotionPushPreview | null>(null);
  const [notionWorkflowId, setNotionWorkflowId] = React.useState<string | null>(
    null,
  );
  const [isNotionPreviewLoading, setIsNotionPreviewLoading] =
    React.useState(false);
  const [notionPushStatus, setNotionPushStatus] = React.useState<
    'idle' | 'pushing' | 'done'
  >('idle');

  const [pushToNotion] = usePushToNotionMutation();
  const { data: notionStatusData } = useGetNotionPushStatusQuery(
    { operationId, workflowId: notionWorkflowId || '' },
    { skip: !notionWorkflowId, pollingInterval: 5_000 },
  );

  // Pre-fill the Notion URL from a previous push (once)
  const hasPrefilledNotionUrl = React.useRef(false);
  React.useEffect(() => {
    if (detail.notion_page_url && !hasPrefilledNotionUrl.current) {
      setTargetPageId(detail.notion_page_url);
      hasPrefilledNotionUrl.current = true;
    }
  }, [detail.notion_page_url]);

  // Handle Notion push status polling results
  const notionToastRef = React.useRef<string | number | null>(null);
  React.useEffect(() => {
    if (!notionStatusData || !notionWorkflowId) return;
    const wfStatus = notionStatusData.status;

    if (wfStatus === 'COMPLETED') {
      toast.success('Pushed to Notion', {
        id: notionToastRef.current ?? undefined,
      });
      setNotionPushStatus('done');
      onPushStatusChange('done');
      setTargetPageId('');
      setNotionWorkflowId(null);
      onPushComplete();
      setTimeout(() => {
        setNotionPushStatus('idle');
        onPushStatusChange('idle');
      }, 2000);
    } else if (
      wfStatus === 'FAILED' ||
      wfStatus === 'TERMINATED' ||
      wfStatus === 'CANCELLED'
    ) {
      const errorMsg = notionStatusData.error
        ? `Push to Notion failed: ${notionStatusData.error}`
        : `Push to Notion ${wfStatus.toLowerCase()}`;
      toast.error(errorMsg, {
        id: notionToastRef.current ?? undefined,
      });
      setNotionPushStatus('idle');
      onPushStatusChange('idle');
      setNotionWorkflowId(null);
      onPushComplete();
    }
  }, [notionStatusData, notionWorkflowId, onPushComplete, onPushStatusChange]);

  const handlePreviewNotionPush = async () => {
    if (isNotionPreviewLoading || notionPushStatus === 'pushing') return;
    let normalizedPageId = '';
    try {
      normalizedPageId = normalizeNotionPageId(targetPageId);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Invalid Notion page ID';
      toast.error(message);
      return;
    }

    try {
      setIsNotionPreviewLoading(true);
      const resp = await pushToNotion({
        operationId,
        targetPageId: normalizedPageId,
        dryRun: true,
      }).unwrap();
      if (isNotionPushPreview(resp)) {
        setNotionPreview(resp);
      }
    } catch (err: unknown) {
      toast.error(
        getApiErrorMessage(
          err,
          'Failed to preview Notion push. Please try again.',
        ),
      );
    } finally {
      setIsNotionPreviewLoading(false);
    }
  };

  const handleConfirmPushToNotion = async () => {
    if (notionPushStatus === 'pushing') return;
    let normalizedPageId = '';
    try {
      normalizedPageId = normalizeNotionPageId(targetPageId);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Invalid Notion page ID';
      toast.error(message);
      return;
    }

    onClose();
    setNotionPushStatus('pushing');
    onPushStatusChange('pushing');

    const toastId = toast.loading('Pushing to Notion...');
    notionToastRef.current = toastId;

    try {
      const resp = await pushToNotion({
        operationId,
        targetPageId: normalizedPageId,
        dryRun: false,
      }).unwrap();

      if (isNotionPushPreview(resp)) return; // shouldn't happen for dryRun=false
      const workflowId = resp.workflow_id;
      if (!workflowId) {
        toast.success('Push to Notion initiated', { id: toastId });
        setNotionPushStatus('done');
        onPushStatusChange('done');
        setTimeout(() => {
          setNotionPushStatus('idle');
          onPushStatusChange('idle');
        }, 2000);
        return;
      }

      toast.loading('Notion push in progress...', { id: toastId });
      setNotionWorkflowId(workflowId);
    } catch (err: unknown) {
      toast.error(
        getApiErrorMessage(
          err,
          'Failed to push to Notion. Please try again.',
        ),
        { id: toastId },
      );
      setNotionPushStatus('idle');
      onPushStatusChange('idle');
    }
  };

  const handleClose = () => {
    onClose();
    setNotionPreview(null);
    setIsNotionPreviewLoading(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} size="4xl">
      <DialogTitle>Push to Notion Readout</DialogTitle>
      <DialogDescription className="!text-xs">
        First, duplicate the{' '}
        <a
          href="https://www.notion.so/Template-Law-Firm-Case-Update-311e7a3220028176b24df4b83035649b"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-600 hover:text-blue-800"
        >
          master Notion template
        </a>{' '}
        into the firm&apos;s hub page, then paste the new page&apos;s ID or URL
        below. Each push overwrites that page&apos;s Case Review rows with the
        latest data.
      </DialogDescription>
      <DialogBody>
        {detail.notion_push_status === 'failed' &&
          detail.notion_push_error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-xs font-medium text-red-800">
                Previous push failed
              </p>
              <p className="mt-0.5 text-xs text-red-700">
                {detail.notion_push_error}
              </p>
            </div>
          )}
        <Field>
          <Label>Notion URL</Label>
          <Input
            autoFocus
            placeholder="Paste Notion page URL..."
            value={targetPageId}
            onChange={(e) => setTargetPageId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && targetPageId.trim()) {
                e.preventDefault();
                if (!notionPreview) {
                  handlePreviewNotionPush();
                } else {
                  handleConfirmPushToNotion();
                }
              }
            }}
          />
        </Field>

        {notionPreview && (
          <div className="mt-4 space-y-3">
            {/* Push info */}
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="text-sm font-medium text-green-800">
                Ready to push
              </div>
              <div className="mt-2 space-y-1.5">
                {!notionPreview.required_columns_present &&
                  notionPreview.missing_required_columns.length > 0 && (
                    <div className="flex items-start gap-2 text-xs text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        Some columns (
                        {notionPreview.missing_required_columns.join(', ')})
                        are missing — those fields will be empty in Notion
                      </span>
                    </div>
                  )}
                {notionPreview.will_create_dynamic_columns_in_notion?.length >
                  0 && (
                  <div className="flex items-start gap-2 text-xs text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {
                        notionPreview.will_create_dynamic_columns_in_notion
                          .length
                      }{' '}
                      new{' '}
                      {notionPreview.will_create_dynamic_columns_in_notion
                        .length === 1
                        ? 'column'
                        : 'columns'}{' '}
                      will be added:{' '}
                      {notionPreview.will_create_dynamic_columns_in_notion
                        .slice(0, 5)
                        .join(', ')}
                      {notionPreview.will_create_dynamic_columns_in_notion
                        .length > 5 && (
                        <span>
                          {' '}
                          +
                          {notionPreview
                            .will_create_dynamic_columns_in_notion.length -
                            5}{' '}
                          more
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sample rows table */}
            {notionPreview.cases_sample?.length > 0 &&
              (() => {
                const allDynamicKeys = Array.from(
                  new Set(
                    notionPreview.cases_sample.flatMap((c) =>
                      Object.keys(c.dynamic_fields || {}),
                    ),
                  ),
                );
                const dynamicKeys = allDynamicKeys.slice(0, 4);
                const hasMoreCols = allDynamicKeys.length > 4;

                return (
                  <div className="rounded-lg border border-zinc-200 overflow-hidden">
                    <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                      <span className="text-xs font-medium text-zinc-700">
                        Sample rows ({notionPreview.cases_sample.length})
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-zinc-100 bg-zinc-50/50">
                            <th className="px-3 py-1.5 text-left font-medium text-zinc-500">
                              Client
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium text-zinc-500">
                              Status
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium text-zinc-500">
                              Update
                            </th>
                            {dynamicKeys.map((key) => (
                              <th
                                key={key}
                                className="px-3 py-1.5 text-left font-medium text-zinc-500"
                              >
                                {key}
                              </th>
                            ))}
                            {hasMoreCols && (
                              <th className="px-3 py-1.5 text-left font-medium text-zinc-500">
                                ...
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {notionPreview.cases_sample.map((c) => (
                            <tr key={c.matter}>
                              <td className="px-3 py-1.5 font-medium text-zinc-900 whitespace-nowrap">
                                {c.matter}
                              </td>
                              <td className="px-3 py-1.5 text-zinc-600 whitespace-nowrap">
                                {c.status}
                              </td>
                              <td
                                className="px-3 py-1.5 text-zinc-600 max-w-[300px] truncate"
                                title={c.commentary || undefined}
                              >
                                {c.commentary || '—'}
                              </td>
                              {dynamicKeys.map((key) => (
                                <td
                                  key={key}
                                  className="px-3 py-1.5 text-zinc-600 max-w-[120px] truncate"
                                >
                                  {c.dynamic_fields?.[key] || '—'}
                                </td>
                              ))}
                              {hasMoreCols && (
                                <td className="px-3 py-1.5 text-zinc-500">
                                  ...
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={() => {
            onClose();
            setNotionPreview(null);
          }}
        >
          Cancel
        </Button>
        <Button
          color="blue"
          disabled={
            !targetPageId.trim() ||
            notionPushStatus === 'pushing' ||
            isNotionPreviewLoading
          }
          onClick={() => {
            if (!notionPreview) {
              handlePreviewNotionPush();
            } else {
              handleConfirmPushToNotion();
            }
          }}
        >
          {isNotionPreviewLoading
            ? 'Previewing...'
            : notionPreview
              ? 'Confirm Push'
              : 'Preview Push'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
